/**
 * Notification Log — the default view. Stat pills, faceted filter bar (source /
 * status / channel), searchable + CSV export, paginated table, and a
 * row → LogDetailDrawer. Ported feature-for-feature from the previous console.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { View, Text, ScrollView, Pressable, TextInput, StyleSheet } from 'react-native';
import { useTheme } from '../context/themeContext';
import { useToast } from '../components/toast';
import Icon from '../components/notif/Icon';
import { StatusBadge, SourceBadge, ChannelRow } from '../components/notif/badges';
import { StatPill, PageHeader, EmptyState, Btn, Banner } from '../components/notif/ui';
import LogDetailDrawer from '../components/notif/logDetailDrawer';
import { Facet } from '../components/notif/facet';
import { getLogs, retryLog } from '../services/notification/logs';
import { getAllUsage } from '../services/notification/usage';
import { transformLogs } from '../utils/logTransformer';
import { relTime, hms } from '../utils/formatters';
import { toCsv, downloadCsv } from '../utils/csv';
import { handleApiError } from '../utils/errorHandler';
import { useT } from '../i18n/useT';
import type { NotificationLogUI } from '../schema/notification';

const STATUS_DOT: Record<string, string> = {
  queued: '#94A3B8', processing: '#1D4ED8', delivered: '#2E7D32', partial: '#F57C00',
  failed: '#C0392B', validation_failed: '#E65100', fallback_skipped: '#6A1B9A', quota_exceeded: '#C0392B',
};
const CHANNELS = ['email', 'whatsapp', 'sms', 'push'];
const PAGE_SIZE = 50;

type BoolMap = Record<string, boolean>;
const isHidden = (m: BoolMap, k: string) => m[k] === false;

export default function LogScreen() {
  const { theme: { colors, font } } = useTheme();
  const toast = useToast();
  const t = useT();
  const STATUS_META: Record<string, { dot: string; label: string }> = Object.fromEntries(
    Object.entries(STATUS_DOT).map(([key, dot]) => [key, { dot, label: t(`log.status.${key}`) }]),
  );

  const [search, setSearch] = useState('');
  const [sources, setSources] = useState<BoolMap>({});
  const [statuses, setStatuses] = useState<BoolMap>({});
  const [channels, setChannels] = useState<BoolMap>({});
  const [page, setPage] = useState(1);

  const [logs, setLogs] = useState<NotificationLogUI[]>([]);
  const [pagination, setPagination] = useState<{ total: number; pages: number } | null>(null);
  const [catalogProducts, setCatalogProducts] = useState<Array<{ productId: string; displayName?: string }>>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [drawer, setDrawer] = useState<NotificationLogUI | null>(null);

  const load = async () => {
    setLoading(true);
    setError(false);
    try {
      const res = await getLogs({ search: search || undefined, page, limit: PAGE_SIZE });
      setLogs(transformLogs(res.logs || []));
      const p = res.pagination;
      setPagination(p ? { total: p.total, pages: p.pages ?? p.totalPages ?? 1 } : null);
    } catch (err) {
      setError(true);
      handleApiError(err, t('log.errLoadFailed'));
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { void load(); }, [page, search]);
  useEffect(() => { getAllUsage().then(d => setCatalogProducts(d.products ?? [])).catch(() => {}); }, []);

  const rows = useMemo(() => logs.filter(n => {
    if (isHidden(sources, n.source)) return false;
    if (isHidden(statuses, n.status)) return false;
    if (n.channels.length > 0 && n.channels.every(c => isHidden(channels, c.type))) return false;
    if (search) {
      const q = search.toLowerCase();
      if (!`${n.jobId} ${n.template} ${n.referenceId}`.toLowerCase().includes(q)) return false;
    }
    return true;
  }), [logs, sources, statuses, channels, search]);

  const counts = useMemo(() => {
    const r = { sources: {} as Record<string, number>, statuses: {} as Record<string, number>, channels: {} as Record<string, number> };
    logs.forEach(n => {
      r.sources[n.source] = (r.sources[n.source] || 0) + 1;
      r.statuses[n.status] = (r.statuses[n.status] || 0) + 1;
      n.channels.forEach(c => { r.channels[c.type] = (r.channels[c.type] || 0) + 1; });
    });
    return r;
  }, [logs]);

  const totalDelivered = logs.filter(n => n.status === 'delivered').length;
  const totalFailed = logs.filter(n => ['failed', 'validation_failed', 'quota_exceeded'].includes(n.status)).length;
  const totalPartial = logs.filter(n => ['partial', 'fallback_skipped'].includes(n.status)).length;

  const sourceOptions = useMemo(() => {
    const labelById = new Map<string, string>();
    catalogProducts.forEach(p => { if (p.displayName) labelById.set(p.productId, p.displayName); });
    const keys = Array.from(new Set([...catalogProducts.map(p => p.productId), ...logs.map(n => n.source)].filter(Boolean))).sort();
    return keys.map(k => ({ key: k, label: labelById.get(k) || k, count: counts.sources[k] || 0 }));
  }, [catalogProducts, logs, counts]);
  const statusOptions = Object.entries(STATUS_META).map(([k, v]) => ({ key: k, label: v.label, dot: v.dot, count: counts.statuses[k] || 0 }));
  const channelOptions = CHANNELS.map(c => ({ key: c, label: c, count: counts.channels[c] || 0 }));

  const handleRetry = (n: NotificationLogUI) => {
    retryLog(n.id)
      .then(() => toast.success(t('log.toastRetryQueued'), { message: n.jobId }))
      .catch(err => handleApiError(err, t('log.errRetryFailed')));
  };

  const exportCsv = () => {
    const csv = toCsv(
      ['Job ID', 'Source', 'Product', 'Reference', 'Template', 'Status', 'Requested', 'Delivered', 'Recipient', 'Queued At'],
      rows.map(n => [
        n.jobId, n.source, n.productId, n.referenceId, n.template, n.status,
        n.requestedChannels.join(' '), n.deliveredChannels.join(' '),
        n.recipient?.email || n.recipient?.phone || n.recipient?.name || '',
        n.timeline.queued ? n.timeline.queued.toISOString() : '',
      ]),
    );
    downloadCsv(`notification-logs-${hms(new Date()).replace(/[: ]/g, '-')}.csv`, csv);
    toast.success(t('log.toastExportReady'), { message: t('log.toastExportBody', { count: rows.length }) });
  };

  const anyActive = !!search || Object.values(sources).some(v => !v) || Object.values(statuses).some(v => !v) || Object.values(channels).some(v => !v);
  const reset = () => { setSearch(''); setSources({}); setStatuses({}); setChannels({}); };

  return (
    <View style={{ flex: 1 }}>
      <View style={[st.head, { backgroundColor: colors.card, borderBottomColor: colors.line }]}>
        <PageHeader
          title={t('log.title')}
          subtitle={t('log.subtitle', { shown: rows.length.toLocaleString(), total: logs.length.toLocaleString() }) + (pagination ? t('log.subtitlePage', { page, pages: pagination.pages }) : '')}
          right={<Btn label={t('common.refresh')} icon="refresh" size="sm" onPress={() => load()} />}
        />
        <View style={st.pills}>
          <StatPill label={t('log.pillTotal')} value={logs.length} tone="neutral" />
          <StatPill label={t('log.pillDelivered')} value={totalDelivered} tone="success" />
          <StatPill label={t('log.pillFailed')} value={totalFailed} tone="error" />
          <StatPill label={t('log.pillPartial')} value={totalPartial} tone="warning" />
        </View>
      </View>

      <View style={[st.filterbar, { backgroundColor: colors.card, borderBottomColor: colors.line }]}>
        <View style={[st.search, { borderColor: colors.line, backgroundColor: colors.paper }]}>
          <Icon name="search" size={14} color={colors.ink3} />
          <TextInput
            value={search}
            onChangeText={setSearch}
            placeholder={t('log.searchPlaceholder')}
            placeholderTextColor={colors.ink4}
            style={[st.searchInput, { color: colors.ink }]}
          />
        </View>
        <Facet label={t('log.facetSource')} options={sourceOptions} value={sources} onChange={setSources} />
        <Facet label={t('log.facetStatus')} options={statusOptions} value={statuses} onChange={setStatuses} />
        <Facet label={t('log.facetChannel')} options={channelOptions} value={channels} onChange={setChannels} />
        {anyActive && <Btn label={t('log.reset')} icon="x" size="sm" onPress={reset} />}
        <View style={{ flex: 1 }} />
        <Text style={{ fontSize: 12, color: colors.ink3, fontFamily: font.mono }}>{t('quotas.countOfTotal', { count: rows.length, total: logs.length })}</Text>
        <Btn label={t('log.exportCsv')} icon="download" size="sm" onPress={exportCsv} disabled={rows.length === 0} />
      </View>

      {error && (
        <View style={{ margin: 16 }}>
          <Banner tone="error" icon="alert"><Text style={{ color: colors.ink2, fontSize: 13 }}>{t('log.loadErrorBanner')}</Text></Banner>
        </View>
      )}

      <ScrollView style={{ flex: 1 }} horizontal={false}>
        {loading ? (
          <View style={{ padding: 40 }}><Text style={{ color: colors.ink3 }}>{t('log.loading')}</Text></View>
        ) : rows.length === 0 ? (
          <EmptyState icon="inbox" title={t('log.emptyTitle')} body={t('log.emptyBody')} />
        ) : (
          <View>
            <View style={[st.tr, st.trHead, { borderBottomColor: colors.line }]}>
              <Text style={[st.th, { color: colors.ink3, width: 140 }]}>{t('log.thCreated')}</Text>
              <Text style={[st.th, { color: colors.ink3, width: 90 }]}>{t('log.thSource')}</Text>
              <Text style={[st.th, { color: colors.ink3, flex: 1 }]}>{t('log.thTemplate')}</Text>
              <Text style={[st.th, { color: colors.ink3, width: 200 }]}>{t('log.thJobId')}</Text>
              <Text style={[st.th, { color: colors.ink3, width: 110 }]}>{t('log.thChannels')}</Text>
              <Text style={[st.th, { color: colors.ink3, width: 130 }]}>{t('common.status')}</Text>
              <Text style={[st.th, { color: colors.ink3, width: 60 }]}>{t('log.thRetry')}</Text>
              <Text style={[st.th, { color: colors.ink3, width: 130 }]}>{t('log.thActions')}</Text>
            </View>
            {rows.map(n => {
              const isFail = n.status === 'failed' || n.status === 'quota_exceeded';
              return (
                <Pressable key={n.id} onPress={() => setDrawer(n)} style={[st.tr, { borderBottomColor: colors.line }, isFail && { backgroundColor: '#FCEAEA22' }]}>
                  <View style={{ width: 140 }}>
                    <Text style={{ color: colors.ink, fontSize: 12 }}>{relTime(n.timeline.queued)}</Text>
                    <Text style={{ color: colors.ink4, fontSize: 11, fontFamily: font.mono }}>{hms(n.timeline.queued)}</Text>
                  </View>
                  <View style={{ width: 90 }}><SourceBadge source={n.source} /></View>
                  <Text style={{ flex: 1, color: colors.ink2, fontSize: 12 }} numberOfLines={1}>{n.template}</Text>
                  <View style={{ width: 200 }}>
                    <Text style={{ color: colors.ink2, fontSize: 11, fontFamily: font.mono }} numberOfLines={1}>{n.jobId}</Text>
                    <Text style={{ color: colors.ink4, fontSize: 11 }} numberOfLines={1}>{n.referenceId}</Text>
                  </View>
                  <View style={{ width: 110 }}><ChannelRow channels={n.channels} /></View>
                  <View style={{ width: 130 }}><StatusBadge status={n.status} /></View>
                  <View style={{ width: 60 }}>
                    <Text style={{ color: n.retryMax > 0 && n.retryCount >= n.retryMax ? '#C0392B' : colors.ink3, fontSize: 12, fontFamily: font.mono }}>
                      {n.retryMax > 0 ? `${n.retryCount}/${n.retryMax}` : '—'}
                    </Text>
                  </View>
                  <View style={{ width: 130, flexDirection: 'row', gap: 6 }}>
                    <Btn label={t('log.view')} size="sm" onPress={() => setDrawer(n)} />
                    {n.status !== 'delivered' && n.status !== 'quota_exceeded' && (
                      <Btn label={t('log.retry')} icon="refresh" size="sm" kind="outline" onPress={() => handleRetry(n)} />
                    )}
                  </View>
                </Pressable>
              );
            })}
          </View>
        )}
      </ScrollView>

      {pagination && pagination.pages > 1 && (
        <View style={[st.pager, { borderTopColor: colors.line, backgroundColor: colors.card }]}>
          <Text style={{ color: colors.ink3, fontSize: 12 }}>
            {t('log.pagerRange', {
              from: ((page - 1) * PAGE_SIZE + 1).toLocaleString(),
              to: Math.min(page * PAGE_SIZE, pagination.total).toLocaleString(),
              total: pagination.total.toLocaleString(),
            })}
          </Text>
          <View style={{ flexDirection: 'row', gap: 8 }}>
            <Btn label={t('log.prev')} icon="chevronLeft" size="sm" disabled={page === 1} onPress={() => setPage(p => p - 1)} />
            <Btn label={t('log.next')} icon="chevronRight" size="sm" disabled={page >= pagination.pages} onPress={() => setPage(p => p + 1)} />
          </View>
        </View>
      )}

      {drawer && <LogDetailDrawer notification={drawer} onClose={() => setDrawer(null)} onRetry={handleRetry} />}
    </View>
  );
}

const st = StyleSheet.create({
  head: { padding: 20, borderBottomWidth: 1, gap: 14 },
  pills: { flexDirection: 'row', gap: 10, flexWrap: 'wrap' },
  filterbar: { flexDirection: 'row', alignItems: 'center', gap: 8, paddingHorizontal: 16, paddingVertical: 10, borderBottomWidth: 1, flexWrap: 'wrap' },
  search: { flexDirection: 'row', alignItems: 'center', gap: 8, paddingHorizontal: 10, height: 34, borderRadius: 2, borderWidth: 1, minWidth: 240 },
  searchInput: { flex: 1, fontSize: 13, outlineStyle: 'none' as any },
  tr: { flexDirection: 'row', alignItems: 'center', gap: 10, paddingHorizontal: 16, paddingVertical: 10, borderBottomWidth: 1 },
  trHead: { paddingVertical: 8 },
  th: { fontSize: 11, fontWeight: '600', textTransform: 'uppercase', letterSpacing: 0.4 },
  pager: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingHorizontal: 16, paddingVertical: 10, borderTopWidth: 1 },
});
